import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useFormDraft } from "@/hooks/useFormDraft";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, User, Building2, MapPin, Globe, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Country list - you can expand this or use a library
const COUNTRIES = [
  "Turkey", "Afghanistan", "Albania", "Algeria", "Argentina", "Armenia", "Australia", 
  "Austria", "Azerbaijan", "Bahrain", "Bangladesh", "Belarus", "Belgium", "Bosnia and Herzegovina",
  "Brazil", "Bulgaria", "Canada", "Chile", "China", "Colombia", "Croatia", "Cyprus", 
  "Czech Republic", "Denmark", "Egypt", "Estonia", "Finland", "France", "Georgia", 
  "Germany", "Greece", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", 
  "Ireland", "Israel", "Italy", "Japan", "Jordan", "Kazakhstan", "Kuwait", "Latvia", 
  "Lebanon", "Lithuania", "Luxembourg", "Malaysia", "Malta", "Mexico", "Moldova", 
  "Montenegro", "Morocco", "Netherlands", "New Zealand", "Norway", "Pakistan", "Poland", 
  "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia", "Serbia", "Singapore", 
  "Slovakia", "Slovenia", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland", 
  "Syria", "Thailand", "Tunisia", "Ukraine", "United Arab Emirates", "United Kingdom", 
  "United States", "Uzbekistan", "Vietnam"
].sort();

const AccountSettings: React.FC<{ onSuccess?: () => void }> = ({ onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();

  const ROLES = [
    { value: "employer", label: t('roles.employer') },
    { value: "laborer", label: t('roles.laborer') },
    { value: "supplier", label: t('roles.supplier') },
    { value: "architect", label: t('roles.architect') },
    { value: "engineer", label: t('roles.engineer') },
    { value: "other", label: t('roles.other') }
  ];

  const accountFormSchema = z.object({
    name: z.string().min(2, { message: t('forms.min_2_chars') }).max(100, { message: t('forms.max_100_chars') }),
    company: z.string().max(200, { message: t('forms.max_200_chars') }).optional(),
    country: z.string().min(1, { message: t('forms.select_required') }),
    city: z.string().max(100, { message: t('forms.max_100_chars') }).optional(),
    selected_role: z.string().min(1, { message: t('forms.select_required') }),
  });

  type AccountFormValues = z.infer<typeof accountFormSchema>;

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      name: "",
      company: "",
      country: "",
      city: "",
      selected_role: "",
    },
  });

  // Persist edits. Gated on the initial server load so the restored draft is
  // applied *after* the form is pre-filled from user metadata (draft wins).
  const { clearDraft } = useFormDraft(form, {
    key: "account-settings",
    enabled: !isInitialLoading,
  });

  // Load current user data and pre-fill form
  const loadUserData = async () => {
    try {
      setIsInitialLoading(true);
      
      // Get fresh user data from Supabase auth
      const { data: { user: currentUser }, error } = await supabase.auth.getUser();
      
      if (error) {
        
        toast.error(t('common.error'));
        return;
      }

      if (currentUser?.user_metadata) {
        const metadata = currentUser.user_metadata;
        
        // Pre-fill form with current metadata
        form.reset({
          name: metadata.name || "",
          company: metadata.company || "",
          country: metadata.country || "",
          city: metadata.city || "",
          selected_role: metadata.selected_role || metadata.role || "", // Handle both field names
        });
        
        
      }
    } catch (error) {
      
      toast.error(t('errors.data_load_error'));
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Load user data on component mount
  useEffect(() => {
    loadUserData();
  }, []);

  // Handle form submission
  const onSubmit = async (values: AccountFormValues) => {
    setIsLoading(true);
    
    try {
      
      
      // Update user metadata using supabase.auth.updateUser
      const { data, error } = await supabase.auth.updateUser({
        data: {
          name: values.name.trim(),
          company: values.company?.trim() || "",
          country: values.country,
          city: values.city?.trim() || "",
          selected_role: values.selected_role,
          // Keep existing metadata that we don't want to overwrite
          email_verified: user?.user_metadata?.email_verified || false,
          phone_verified: user?.user_metadata?.phone_verified || false,
        }
      });

      if (error) {
        
        throw error;
      }

      
      
      // Refresh user data in auth context
      if (refreshUser) {
        await refreshUser();
      }
      
      // Saved — discard the draft.
      clearDraft();

      // Show success message
      toast.success(t('account.profile_updated'), {
        description: t('account.profile_updated_desc'),
      });

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error: any) {
      
      
      // Handle specific error messages
      let errorMessage = t('account.error_updating_profile');
      
      if (error?.message?.includes('invalid_user_metadata')) {
        errorMessage = t('account.invalid_user_data');
      } else if (error?.message?.includes('network')) {
        errorMessage = t('account.check_internet');
      }
      
      toast.error(t('account.update_error'), {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">{t('account.profile_loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <User className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">{t('account.account_info')}</h3>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Email Field - Read Only */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t('account.email_address')}
            </label>
            <Input
              value={user?.email || ""}
              disabled
              className="bg-muted cursor-not-allowed"
              placeholder={t('account.email_address')}
            />
            <p className="text-xs text-muted-foreground">
              {t('account.email_cannot_change')}
            </p>
          </div>

          {/* Name Field */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>{t('account.name_surname')} *</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder={t('auth.name_surname')} 
                    {...field} 
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Role Field */}
          <FormField
            control={form.control}
            name="selected_role"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center space-x-2">
                  <Briefcase className="h-4 w-4" />
                  <span>{t('account.your_role')} *</span>
                </FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  disabled={isLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('account.select_role')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Company Field */}
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4" />
                  <span>{t('account.company')}</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder={t('account.company_optional')} 
                    {...field} 
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Country Field */}
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>{t('account.country')} *</span>
                </FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  disabled={isLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('account.select_country')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* City Field */}
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>{t('account.city')}</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder={t('account.city_optional')} 
                    {...field} 
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <div className="pt-4">
            <Button 
              type="submit" 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('account.updating')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('account.save_changes')}
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AccountSettings; 